import { useState, useEffect } from 'react';
import { publicApi } from '@/lib/api';
import { Instagram, Linkedin } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useReveal, useParallax } from '@/hooks/useScrollAnimation';


export default function FooterSection() {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();
  const [ref, visible] = useReveal(0.05);
  const brandRef = useParallax(0.1);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
  publicApi.getSettings()
    .then((res) => setSettings(res.data))
    .catch(console.error);
  }, []);

  return (
    <footer data-testid="footer-section" className="bg-black border-t border-[#222] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div ref={brandRef}>
          <h2 data-testid="footer-brand" className="footer-brand text-[#FFD700]/10 select-none mb-16">PETIT HEM</h2>
        </div>
        <div ref={ref} className={`reveal flex flex-col md:flex-row justify-between items-start gap-10 border-t border-[#222] pt-10 ${visible ? 'visible' : ''}`}>
          {/* Left - Brand + Social */}
          <div>
            <p className="text-xl text-[#FFD700] mb-1" style={{fontFamily:'Bebas Neue', letterSpacing:'0.05em'}}>Petit Hem</p>
            <p className="text-sm text-[#B0B0B0] mb-4" style={{fontFamily:'Manrope'}}>{t('footer.subtitle')}</p>
            <div className="flex items-center gap-4">
              {settings?.instagram_url && (
                <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer"
                  data-testid="footer-instagram"
                  className="text-[#B0B0B0] hover:text-[#FFD700] transition-colors duration-300"
                  aria-label="Instagram">
                  <Instagram size={20} />
                </a>
              )}
            </div>
          </div>

          {/* Center - Nav */}
          <div className="flex gap-8">
            <a href="#menu" data-testid="footer-link-menu"
              className="text-sm tracking-widest uppercase text-[#B0B0B0] hover:text-[#FFD700] transition-colors font-medium">
              {t('header.menu')}
            </a>
            <a href="#contact" data-testid="footer-link-contact"
              className="text-sm tracking-widest uppercase text-[#B0B0B0] hover:text-[#FFD700] transition-colors font-medium">
              {t('header.visitUs')}
            </a>
          </div>

          {/* Right - Copyright + Developer */}
          <div className="text-right">
            <p data-testid="footer-copyright" className="text-sm text-[#B0B0B0] mb-4">
              &copy; {currentYear} Petit Hem. {t('footer.copyright')}
            </p>
            {settings?.developer_linkedin_url && (
              <a href={settings.developer_linkedin_url} target="_blank" rel="noopener noreferrer"
                data-testid="footer-developer"
                className="group inline-flex items-center gap-3 relative overflow-hidden">
                <span className="relative flex items-center gap-2.5 text-[11px] text-[#444] group-hover:text-[#999] tracking-[0.25em] uppercase transition-colors duration-700" style={{fontFamily:'Manrope'}}>
                  <span className="developer-orbit relative w-4 h-4 flex-shrink-0">
                    <Linkedin size={10} className="absolute inset-0 m-auto text-[#0A66C2] group-hover:text-[#FFD700] transition-colors duration-500" />
                    <span className="developer-ring absolute inset-0 border border-[#333] group-hover:border-[#FFD700]/40 rounded-full transition-colors duration-500" />
                  </span>
                  created by
                  <span className="text-[#888] group-hover:text-[#FFD700] font-semibold transition-colors duration-500">Hari Vinayak</span>
                </span>
                <span className="absolute bottom-0 left-0 h-[1px] w-0 bg-gradient-to-r from-transparent via-[#FFD700]/50 to-transparent group-hover:w-full transition-all duration-700" />
              </a>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}
